import React, { createContext, useContext, useEffect, useState, useMemo, useCallback } from 'react';
import { applyTheme } from './theme';
import { isPlatformPublicLightModeOnly } from './platformPublicLightModeOnly';
import logger from './logger';

const LIGHT_MODE_ONLY = isPlatformPublicLightModeOnly();

// Dark Mode Context
const DarkModeContext = createContext();

// Hook to use dark mode
export const useDarkMode = () => {
  const context = useContext(DarkModeContext);
  if (!context) {
    throw new Error('useDarkMode must be used within a DarkModeProvider');
  }
  return context;
};

// Dark Mode Provider Component
export const DarkModeProvider = ({ children }) => {
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    if (!isInitialized) {
      if (LIGHT_MODE_ONLY) {
        setIsDarkMode(false);
        if (typeof localStorage !== 'undefined') {
          localStorage.setItem('besper-theme', 'light');
        }
        applyTheme('light', false).finally(() => setIsInitialized(true));
        return;
      }
      setIsInitialized(true);
    }
  }, [isInitialized]);

  // Apply theme to document when state changes (DOM only, no backend update)
  useEffect(() => {
    if (isInitialized) {
      // Only update DOM, don't update backend (backend updates happen in toggle functions)
      const root = document.documentElement;
      const actualTheme = isDarkMode ? 'dark' : 'light';
      root.setAttribute('data-theme', actualTheme);
    }
  }, [isDarkMode, isInitialized]);

  const toggleDarkMode = useCallback(async () => {
    if (LIGHT_MODE_ONLY) {
      return;
    }
    const currentTheme = isDarkMode ? 'dark' : 'light';
    const newTheme = currentTheme === 'light' ? 'dark' : 'light';
    logger.debug('DARK-MODE', 'Toggling theme', { from: currentTheme, to: newTheme });
    setIsDarkMode(newTheme === 'dark');
    await applyTheme(newTheme, true);
  }, [isDarkMode]);

  const setLightMode = useCallback(async () => {
    logger.debug('DARK-MODE', 'Setting light mode');
    setIsDarkMode(false);
    await applyTheme('light', LIGHT_MODE_ONLY ? false : true);
  }, []);

  const setDarkModeValue = useCallback(async (dark) => {
    if (LIGHT_MODE_ONLY && dark) {
      return;
    }
    const newTheme = dark ? 'dark' : 'light';
    logger.debug('DARK-MODE', 'Setting theme', { theme: newTheme });
    setIsDarkMode(dark);
    await applyTheme(newTheme, LIGHT_MODE_ONLY ? false : true);
  }, []);

  const value = useMemo(() => ({
    isDarkMode,
    toggleDarkMode,
    setLightMode,
    setDarkMode: setDarkModeValue
  }), [isDarkMode, toggleDarkMode, setLightMode, setDarkModeValue]);

  return (
    <DarkModeContext.Provider value={value}>
      {children}
    </DarkModeContext.Provider>
  );
};

export default DarkModeProvider;